import { useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { buildCityReportPdf } from "./buildPdf";

interface UsePdfGeneratorArgs {
  collectionId: string | null | undefined;
}

interface UsePdfGeneratorResult {
  isGenerating: boolean;
  generate: () => Promise<void>;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function usePdfGenerator({
  collectionId,
}: UsePdfGeneratorArgs): UsePdfGeneratorResult {
  const utils = trpc.useUtils();
  const [isGenerating, setIsGenerating] = useState(false);

  const generate = async () => {
    if (!collectionId || isGenerating) return;
    setIsGenerating(true);
    try {
      const data = await utils.physicalMapping.getReportPdfData.fetch({
        collectionId,
      });
      if (data.stations.length === 0) {
        toast.error("No approved main stations to include in the report.");
        return;
      }
      const blob = await buildCityReportPdf(data);
      const cityName = data.collection.client_name ?? data.collection.title;
      triggerDownload(blob, `Equip Report — ${cityName}.pdf`);
      toast.success("PDF report downloaded");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to generate PDF";
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  return { isGenerating, generate };
}
